import { useEffect, useState } from 'react'
import { fetchMeasurements, saveMeasurement, type Measurement } from '../lib/api'
import { Button, ErrorBanner, Group, Screen, Spinner } from '../components/ui'

const WEIGHT_GOAL = 86
const WAIST_GOAL = 95

export function MeasureScreen() {
  const [rows, setRows] = useState<Measurement[] | null>(null)
  const [progress, setProgress] = useState<{ weight_kg: number; waist_cm: number } | null>(null)
  const [weight, setWeight] = useState('')
  const [waist, setWaist] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function reload() {
    try {
      const data = await fetchMeasurements()
      setRows(data.measurements)
      setProgress(data.progress)
      setError(null)
    } catch (cause) {
      setError((cause as Error).message)
    }
  }

  useEffect(() => {
    void reload()
  }, [])

  if (!rows) return <Spinner label="Загружаю замеры…" />

  const last = rows[rows.length - 1]

  async function submit() {
    setSaving(true)
    try {
      await saveMeasurement({
        weightKg: weight ? Number(weight.replace(',', '.')) : undefined,
        waistCm: waist ? Number(waist.replace(',', '.')) : undefined,
      })
      setWeight('')
      setWaist('')
      await reload()
    } catch (cause) {
      setError((cause as Error).message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Screen title="Замеры">
      {error && <ErrorBanner message={error} onClose={() => setError(null)} />}

      <div className="grid grid-cols-2 gap-2">
        <MetricCard
          label="Вес"
          value={last?.weight_kg ?? null}
          unit="кг"
          delta={progress?.weight_kg}
          goal={WEIGHT_GOAL}
        />
        <MetricCard
          label="Талия"
          value={last?.waist_cm ?? null}
          unit="см"
          delta={progress?.waist_cm}
          goal={WAIST_GOAL}
        />
      </div>

      <p className="footnote px-4">
        Талия важнее веса: вес прыгает от воды, талия реагирует только на жир.
      </p>

      {rows.length > 1 && <TrendChart rows={rows} />}

      <Group
        caption="Внести замер"
        footnote="Утром, после туалета, до еды. Талию — на уровне пупка, на выдохе, не втягивая."
      >
        <div className="flex flex-col gap-3 px-4 py-3.5">
          {/* Поля во всю ширину: фиксированная ширина в 112 точек не помещалась
              вместе с кнопкой и выдавливала экран вбок. */}
          <Field label="Вес, кг" value={weight} onChange={setWeight} placeholder="96,0" />
          <Field label="Талия, см" value={waist} onChange={setWaist} placeholder="105" />
          <Button variant="filled" full onClick={submit} disabled={saving || (!weight && !waist)}>
            {saving ? 'Сохраняю…' : 'Сохранить'}
          </Button>
        </div>
      </Group>
    </Screen>
  )
}

/**
 * Числовое поле держит строку, а не число: на «96,» состояние прошло бы через
 * Number() и React вернул бы поле к «96», стерев запятую под пальцами.
 */
function Field({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string
  value: string
  onChange: (next: string) => void
  placeholder: string
}) {
  return (
    <label className="flex items-center gap-3">
      <span className="w-24 shrink-0 text-[15px] text-ink-2">{label}</span>
      <input
        inputMode="decimal"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="numerals min-w-0 flex-1 rounded-[10px] border border-ink-separator bg-ink-surface2 px-3 py-2.5 text-[17px]"
      />
    </label>
  )
}

function MetricCard({
  label,
  value,
  unit,
  delta,
  goal,
}: {
  label: string
  value: number | null
  unit: string
  delta?: number
  goal: number
}) {
  return (
    <div className="rounded-card bg-ink-surface px-3.5 py-3">
      <div className="footnote">{label}</div>
      <div className="numerals mt-0.5 text-[28px] font-semibold leading-tight">
        {value === null ? '—' : value.toLocaleString('ru-RU')}
        <span className="ml-1 text-[15px] font-normal text-ink-2">{unit}</span>
      </div>
      <div className="footnote mt-0.5 leading-tight">
        {delta !== undefined && delta !== 0 && (
          <span className={delta < 0 ? 'text-exercise' : 'text-sys-orange'}>
            {delta > 0 ? '+' : ''}
            {delta.toFixed(1)} от старта ·{' '}
          </span>
        )}
        цель {goal}
      </div>
    </div>
  )
}

/** Линия веса и талии по неделям — обе в своей шкале, чтобы видеть форму тренда. */
function TrendChart({ rows }: { rows: Measurement[] }) {
  const weights = rows.filter((row) => row.weight_kg !== null)
  const waists = rows.filter((row) => row.waist_cm !== null)

  const line = (values: number[], color: string) => {
    if (values.length < 2) return null
    const min = Math.min(...values)
    const max = Math.max(...values)
    const span = max - min || 1
    const points = values
      .map((value, index) => {
        const x = (index / (values.length - 1)) * 280
        const y = 70 - ((value - min) / span) * 55
        return `${x.toFixed(1)},${y.toFixed(1)}`
      })
      .join(' ')
    return (
      <polyline points={points} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
    )
  }

  return (
    <Group caption="Динамика">
      <div className="px-4 py-3.5">
        <svg viewBox="0 0 280 80" className="block w-full">
          {line(
            weights.map((row) => Number(row.weight_kg)),
            'var(--color-sys-blue)',
          )}
          {line(
            waists.map((row) => Number(row.waist_cm)),
            'var(--color-sys-purple)',
          )}
        </svg>
        <div className="footnote mt-2 flex gap-4">
          <span className="text-sys-blue">— вес</span>
          <span className="text-sys-purple">— талия</span>
        </div>
      </div>
    </Group>
  )
}
